/* noms+ · Wirkkreis · assets/nplus-wirkkreis.js
   Ein Custom Element pro Section-Instanz. Kein jQuery, keine
   Abhängigkeiten. Läuft auch im Theme-Editor sauber neu an,
   weil Custom Elements beim Neu-Rendern automatisch greifen. */

class NplusWirkkreis extends HTMLElement {
  connectedCallback() {
    // Wird das Script inline und ohne defer geladen, upgradet das
    // Element bereits beim Öffnen-Tag – die Kinder sind dann noch
    // nicht geparst. Deshalb zur Sicherheit auf das Dokument warten.
    if (!this.querySelector('[data-wk-panel]') && document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  init() {
    if (this.ready) return;
    this.ready = true;
    this.tabs   = Array.from(this.querySelectorAll('[data-wk-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-wk-panel]'));
    this.labels = Array.from(this.querySelectorAll('[data-wk-label]'));
    this.arc    = this.querySelector('.np-wk__ring-fg');
    this.count  = this.panels.length;
    if (!this.count) return;

    this.delay  = parseInt(this.dataset.autoplay || '0', 10);
    this.index  = 0;

    // ------------------------------------------------------------
    // Wischen auf schmalen Schirmen
    // ------------------------------------------------------------
    // Unter 1024 px liegen alle Panels nebeneinander in einem
    // Scroll-Snap-Streifen. Das nutzt die native Wischmechanik des
    // Systems - Momentum, Gummiband, Zurueckschnappen - statt eine
    // eigene Touch-Logik zu bauen, die sich nie so anfuehlt.
    // In diesem Modus wird hidden NICHT gesetzt: ein sichtbares, aber
    // als hidden markiertes Panel waere fuer Screenreader unsichtbar.
    this.strip = this.querySelector('.np-wk__panels');
    this.mq = window.matchMedia('(max-width: 1024px)');
    this.swipe = this.mq.matches;
    this.mq.addEventListener('change', (e) => {
      this.swipe = e.matches;
      this.applyPanels();
      if (this.swipe) this.scrollToIndex(true);
    });

    if (this.strip) {
      let t;
      this.strip.addEventListener('scroll', () => {
        if (!this.swipe) return;
        clearTimeout(t);
        t = setTimeout(() => this.syncFromScroll(), 90);
      }, { passive: true });
    }

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => { this.select(parseInt(tab.dataset.wkTab, 10)); this.stop(); });
      tab.addEventListener('keydown', (e) => this.onKey(e));
    });

    this.select(0, true);
    if (this.delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.start();
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin',    () => this.pause());
    }
  }

  disconnectedCallback() { this.stop(); }

  select(i, silent) {
    this.index = ((i % this.count) + this.count) % this.count;

    this.applyPanels();
    if (this.swipe && !silent) this.scrollToIndex();
    this.paint();

    // Fokus nur bei echter Nutzerinteraktion umsetzen, nicht beim Laden.
    if (!silent) {
      const active = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true' && t.offsetParent !== null);
      if (active && document.activeElement && this.contains(document.activeElement)) active.focus();
    }
  }

  /** Kreis, Chips, Punkte und Beschriftungen auf den Stand bringen. */
  paint() {
    this.tabs.forEach((t) => {
      const on = parseInt(t.dataset.wkTab, 10) === this.index;
      // Kreis und Chips sind echte Tabs, die Punkte darunter nur eine
      // Zweitnavigation – sonst meldet der Screenreader drei Tablisten.
      if (t.getAttribute('role') === 'tab') {
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      } else {
        t.setAttribute('aria-current', on ? 'true' : 'false');
      }
    });
    this.labels.forEach((l, n) => l.setAttribute('data-active', n === this.index ? 'true' : 'false'));

    if (this.arc) {
      const c = 2 * Math.PI * this.arc.r.baseVal.value;
      const filled = c * ((this.index + 1) / this.count);
      this.arc.style.strokeDasharray = filled + ' ' + (c - filled);
    }
  }

  /** Im Wischmodus bleiben alle Panels sichtbar, sonst nur das aktive. */
  applyPanels() {
    this.panels.forEach((p, n) => { p.hidden = this.swipe ? false : n !== this.index; });
  }

  scrollToIndex(instant) {
    if (!this.strip || !this.panels[this.index]) return;
    const el = this.panels[this.index];
    this.strip.scrollTo({
      left: el.offsetLeft - this.strip.offsetLeft,
      behavior: instant ? 'auto' : 'smooth'
    });
  }

  /** Nach dem Wischen: aktives Panel aus der Scrollposition ableiten. */
  syncFromScroll() {
    if (!this.strip) return;
    const mid = this.strip.scrollLeft + this.strip.clientWidth / 2;
    let best = 0, bestD = Infinity;
    this.panels.forEach((p, n) => {
      const c = p.offsetLeft - this.strip.offsetLeft + p.offsetWidth / 2;
      const d = Math.abs(c - mid);
      if (d < bestD) { bestD = d; best = n; }
    });
    if (best !== this.index) {
      // select() ohne erneutes Scrollen - der Finger fuehrt, nicht das Script
      this.index = best;
      this.applyPanels();
      this.paint();
      this.stop();
    }
  }

  onKey(e) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (keys[e.key]) { e.preventDefault(); this.select(this.index + keys[e.key]); this.stop(); }
    else if (e.key === 'Home') { e.preventDefault(); this.select(0); this.stop(); }
    else if (e.key === 'End')  { e.preventDefault(); this.select(this.count - 1); this.stop(); }
  }

  start() { this.pause(); this.timer = setInterval(() => this.select(this.index + 1, true), this.delay); }
  pause() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
  stop()  { this.pause(); this.delay = 0; }
}

if (!customElements.get('nplus-wirkkreis')) {
  customElements.define('nplus-wirkkreis', NplusWirkkreis);
}
